// ============================================================================
// SensitivitySlider — the action / body-part sensitivity control, presented
// EXACTLY like the product ships it (customer-app/src/screens/Sliders.tsx):
// the same 0..3 stops and the same plain-language legend a non-technical parent
// reads. The raw value passed up is the SAME integer the pipeline's
// gate_by_sensitivity consumes, so the demo's gating is the real gating.
//
// SCALE (matches customer-app Sliders.tsx SENSITIVITY_STOPS):
//   0 Off              — this axis won't trigger alerts on its own.
//   1 High sensitivity — catch the suggestive/borderline too (swimsuits,
//                        underwear, suggestive poses). The MOST alerts.
//   2 Balanced         — the recommended default.
//   3 Explicit only    — only explicit nudity/acts. The FEWEST alerts.
//
// IMPORTANT: the PRODUCT label order is the inverse of the raw pipeline int
// meaning (pipeline int 3 = lenient/catch-more; int 1 = strict/explicit-only).
// To keep the demo honest, this component maps UI position -> the pipeline int
// the gate actually wants. The mapping table is below and is the single place
// that reconciles the two. (See gating.ts header.)
// ============================================================================

export type UiStop = 0 | 1 | 2 | 3;

interface Stop {
  ui: UiStop; // slider position (matches product label order)
  pipelineInt: number; // what gate_by_sensitivity consumes
  label: string;
  blurb: string;
}

// Product labels (Sliders.tsx) on the left; pipeline ints (gate_by_sensitivity)
// on the right. UI "High sensitivity" (most alerts) == pipeline lenient(3);
// UI "Explicit only" (fewest) == pipeline strict(1).
export const STOPS: Stop[] = [
  {
    ui: 0,
    pipelineInt: 0,
    label: "Off",
    blurb: "This axis won't trigger alerts on its own.",
  },
  {
    ui: 1,
    pipelineInt: 3, // lenient — catch more
    label: "High sensitivity",
    blurb:
      "Catch the suggestive / borderline too — swimsuits, underwear, suggestive poses. The most alerts.",
  },
  {
    ui: 2,
    pipelineInt: 2, // standard
    label: "Balanced",
    blurb:
      "The recommended default — clear nudity or sexual acts, fewer borderline calls.",
  },
  {
    ui: 3,
    pipelineInt: 1, // strict — explicit only
    label: "Explicit only",
    blurb:
      "Only explicit nudity / sexual acts trigger an alert. The fewest, most certain alerts.",
  },
];

export function uiToPipelineInt(ui: UiStop): number {
  return STOPS.find((s) => s.ui === ui)?.pipelineInt ?? 2;
}

export function SensitivitySlider({
  title,
  subtitle,
  value,
  onChange,
}: {
  title: string;
  subtitle: string;
  value: UiStop;
  onChange: (v: UiStop) => void;
}) {
  const current = STOPS.find((s) => s.ui === value);
  return (
    <div className="ctrl">
      <div className="ctrl-head">
        <span className="ctrl-title">{title}</span>
        <span className="ctrl-value">{current?.label}</span>
      </div>
      <div className="ctrl-sub">{subtitle}</div>

      <input
        type="range"
        className="slider"
        min={0}
        max={3}
        step={1}
        value={value}
        aria-label={title}
        onChange={(e) => onChange(Number(e.target.value) as UiStop)}
      />

      <div className="ticks">
        {STOPS.map((s) => (
          <button
            key={s.ui}
            type="button"
            className={"tick" + (s.ui === value ? " on" : "")}
            onClick={() => onChange(s.ui)}
          >
            {s.label}
          </button>
        ))}
      </div>

      <p className="ctrl-legend">
        <strong>{current?.label}:</strong> {current?.blurb}
      </p>
    </div>
  );
}
